import { objectToArray } from './common'

const WORDS_DELIMITER = '-'

enum SlugDelimiters {
  slash = '/',
  empty = '',
}

/**
 * Splits slug input into slugLine and wildpart
 * Each part also devides into separate words (hyphen stands for the delimiter)
 *
 * @param value - a slug input
 * @param selectionStart - cursor position
 *
 * @returns [
 *  array of slugline words,
 *  array of wildpart words,
 *  active word (inferred from the cursor position):
 *    positive - for slugline words (starting from 0)
 *    negative - for wildpart words (starting from -1),
 *  slug delimiter - "/" or ""
 * ]
 */
export const splitSlug = (
  value: string,
  selectionStart: number = value.length
): [string[], string[], number, SlugDelimiters] => {
  const [slugLine = '', wildpart = ''] = value.split(SlugDelimiters.slash)
  const words = slugLine.split(WORDS_DELIMITER)
  const wildpartWords = wildpart.split(WORDS_DELIMITER)
  const activeSlugterm =
    selectionStart <= slugLine.length
      ? slugLine.slice(0, selectionStart).split(WORDS_DELIMITER).length - 1
      : -wildpart.split(WORDS_DELIMITER).length // When cursor is over the wildpart
  const slugDelimiter = wildpart ? SlugDelimiters.slash : SlugDelimiters.empty
  return [words, wildpartWords, activeSlugterm, slugDelimiter]
}

export const isSlugline = (value: string): boolean =>
  !!value.endsWith(SlugDelimiters.slash)

export const substituteActiveSlugterm = (
  value: string,
  selectionStart: number,
  slugValue: string
): string => {
  const [words, wildpartWords, activeSlugterm, slugDelimiter] = splitSlug(
    value,
    selectionStart
  )
  const substitutedValue = isSlugline(slugValue)
    ? `${slugValue}${wildpartWords.join(WORDS_DELIMITER)}`
    : activeSlugterm >= 0
    ? `${objectToArray({ ...words, [activeSlugterm]: slugValue }).join(
        WORDS_DELIMITER
      )}${slugDelimiter}${wildpartWords.join(WORDS_DELIMITER)}`
    : `${words.join(WORDS_DELIMITER)}${slugDelimiter}${objectToArray({
        ...wildpartWords,
        [-activeSlugterm - 1]: slugValue,
      }).join(WORDS_DELIMITER)}`
  return substitutedValue
}

export const pickValueToSearch = (inputValue, selectionStart) => {
  const [words, wildpartWords, activeSlugterm] = splitSlug(
    inputValue,
    selectionStart
  )
  return activeSlugterm >= 0
    ? words.join(WORDS_DELIMITER)
    : wildpartWords.join(WORDS_DELIMITER)
}
